// src/contexts/SessionContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  ChatSession,
  ChatMessage,
  ChatSessionManager,
  MessageRole,
  UserMessageAction,
} from "../interfaces/chatSession";
import { MemorySessionManager } from "../services/memorySessionManager";
import { useAgent } from "./AgentContext";
import { useAuth } from "./AuthContext";
import { useEntitlements } from "../hooks/useEntitlements";
import {
  SessionWriteResult,
  deleteSessionState,
  fetchSessionState,
  saveSessionState,
} from "../api/sessionPersistenceApi";
import {
  PERSISTED_SESSION_SCHEMA_VERSION,
  buildPersistedPayload,
  rehydrateSessionPayload,
} from "../services/sessionSnapshot";
import { agentAwareApiAdapter } from "../api/agentAwareApiAdapter";
import { agentModelServiceFactory } from "../services/agentModelServiceFactory";
import { AgentAwareModelService } from "../services/agentAwareModelService";
import { createLogger } from "../utils/logger";
import { track } from "../utils/analytics";

const log = createLogger("SessionContext");

/**
 * Trailing-edge debounce for a session write. Long enough that a burst of
 * per-chunk updates collapses into one request, short enough that a mutation
 * made just before the pane is closed still reaches the backend.
 */
const SAVE_DEBOUNCE_MS = 1500;

/**
 * How many times a write that never reached the backend is re-armed before it
 * is dropped. A pane that is simply offline must not keep a timer spinning for
 * the rest of the day - the next session mutation starts the count over.
 */
const MAX_CONSECUTIVE_SAVE_RETRIES = 3;

/**
 * How many debounce ticks a save waits out while its agent is still streaming.
 * Generous enough to cover a long answer, bounded so a stream that never
 * reports completion cannot suppress the write indefinitely.
 */
const MAX_STREAMING_DEFERRALS = 400;

/**
 * Context properties exposed to components
 */
interface SessionContextType {
  // Session state
  currentSession: ChatSession;

  // Message operations
  addUserMessage: (content: string, documentContext?: any) => ChatMessage;
  addAssistantMessage: (content: string) => ChatMessage;
  addUserMessageForAgent: (
    agentId: string,
    content: string,
    documentContext?: any,
    action?: UserMessageAction
  ) => ChatMessage;
  addAssistantMessageForAgent: (agentId: string, content: string) => ChatMessage;
  updateMessage: (id: string, updates: Partial<Omit<ChatMessage, "id">>) => void;
  updateMessageForAgent: (
    agentId: string,
    id: string,
    updates: Partial<Omit<ChatMessage, "id">>
  ) => void;

  // Conversation management
  clearConversation: () => void;
  removeLastMessagePair: () => void;
  removeLastMessagePairForAgent: (agentId: string) => void;
  getAgentSession: (agentId: string) => ChatSession | undefined;

  // Contract type management
  setContractType: (contractType: string) => void;
  getContractType: () => string | null;

  // Selected playbook management (Catylex multi-playbook tenants)
  setSelectedPlaybook: (playbook: { id: string; name: string } | null) => void;
  getSelectedPlaybook: () => { id: string; name: string } | null;

  // Learning tip tracking (for rotation without repeats within session)
  markTipAsSeen: (tipId: string) => void;
  getSeenTipIds: () => Set<string>;
  resetSeenTips: () => void;

  // Utility
  getConversationHistory: () => { role: string; content: string | unknown[] }[];

  // Session persistence (stored conversations, restored on launch)
  /**
   * True until the launch-time restore has settled, one way or the other.
   * The chat must not paint while it is true or the user sees an empty
   * welcome screen flip into a conversation.
   */
  isSessionRestorePending: boolean;
  /** Whether any agent came back with a stored conversation on this launch. */
  hasRestoredSession: boolean;
  /**
   * Supply the "is this agent generating" predicate. Generation state lives in
   * ModelContext, which is mounted below this provider, so it cannot be read
   * from here directly - the consumer that has both hands it over instead.
   * Pass null to withdraw it.
   */
  registerStreamingProbe: (probe: ((agentId: string) => boolean) | null) => void;
  /** Discard the stored conversation for an agent (New Chat). */
  deleteStoredSession: (agentId: string) => void;
  /**
   * Whether to show the one-off notice explaining what a restored conversation
   * does and does not carry over. Shown once per launch, not once per agent:
   * the limitations are a property of the restore, not of the conversation the
   * user happens to be looking at, so switching agents must not raise it again.
   */
  showRestoreNotice: boolean;
  /** Dismiss the restore notice for the rest of this launch. */
  dismissRestoreNotice: () => void;
}

// Create the context with undefined default value
const SessionContext = createContext<SessionContextType | undefined>(undefined);

/**
 * Props for the SessionProvider component
 */
interface SessionProviderProps {
  children: React.ReactNode;
  // Optional prop to inject a specific session manager implementation
  sessionManager?: ChatSessionManager;
}

/**
 * Provider component that wraps the app and makes session data available
 */
export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
  sessionManager: externalSessionManager,
}) => {
  // Get the current agent from the AgentContext
  const { currentAgent, availableAgents, setCurrentAgentById } = useAgent();

  // Auth state drives the whole persistence lifecycle: nothing is read or
  // written without a token, and the entitlement decides whether the feature
  // exists for this tenant at all.
  const { accessToken, authCompleted, canAccessBackend, accessDecision, userInfo } = useAuth();
  const { sessionPersistence } = useEntitlements();

  // Use the provided session manager or create a memory one
  const [sessionManager] = useState<ChatSessionManager>(
    () => externalSessionManager || new MemorySessionManager()
  );

  // State to force re-renders when session changes
  const [updateCounter, setUpdateCounter] = useState(0);

  // Restore state. Starts pending so the very first paint is already held: by
  // the time we know whether there is anything to restore, the decision is in.
  const [isSessionRestorePending, setIsSessionRestorePending] = useState(true);
  const [restoredAgentIds, setRestoredAgentIds] = useState<string[]>([]);
  const restoreStartedRef = useRef(false);

  // The restore notice is raised once per launch and never again, even if
  // several agents were restored or the user switches between them.
  const [showRestoreNotice, setShowRestoreNotice] = useState(false);
  const restoreNoticeShownRef = useRef(false);

  // Saves run from timers, long after the render that scheduled them, so they
  // read the live values through refs rather than a captured render's copy.
  const accessTokenRef = useRef(accessToken);
  const persistenceEnabledRef = useRef(sessionPersistence);
  const currentAgentIdRef = useRef(currentAgent.id);

  // Who the in-memory conversations belong to. A token refresh can land on a
  // different Microsoft account without unmounting anything (the pane already
  // watches for that to reset classification), and the backend files a session
  // under whichever oid signed the request - so a write made after a switch
  // would hand one person's transcript to the next. Every write is checked
  // against this, and a change resets the whole persistence lifecycle.
  const sessionOwnerRef = useRef<string | null>(null);

  // One debounce timer per agent: four agents can be mutated in parallel, and a
  // write for one must never carry (or cancel) another's transcript.
  const saveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const saveRetriesRef = useRef<Map<string, number>>(new Map());
  const saveDeferralsRef = useRef<Map<string, number>>(new Map());

  // A save already awaiting its response can re-arm a timer after teardown,
  // which would outlive the tree the session manager belongs to.
  const isUnmountedRef = useRef(false);

  // Bumped whenever an agent's stored session is deliberately discarded. A save
  // already awaiting its response cannot be recalled, so it compares the epoch
  // it started under against the current one and throws its result away if the
  // conversation was cleared in the meantime - otherwise a PUT in flight when
  // New Chat is pressed re-creates the row the DELETE just removed.
  const writeEpochRef = useRef<Map<string, number>>(new Map());

  // Agents with a write already awaiting its response. Two overlapping PUTs to
  // the same row are resolved by arrival order, so a slow one carrying an older
  // transcript could land last and roll the stored session back; serializing
  // per agent keeps the newest write the winning one.
  const savesInFlightRef = useRef<Set<string>>(new Set());

  // Agents whose stored state we actually know. A save replaces the whole row,
  // so writing before a conclusive read would overwrite a stored conversation
  // with whatever an empty pane has accumulated since launch. An agent is armed
  // only once its read came back - either with a session or with a definite
  // "nothing stored" - so a failed or skipped restore leaves saving off rather
  // than destroying what is there.
  const saveArmedAgentsRef = useRef<Set<string>>(new Set());

  const streamingProbeRef = useRef<((agentId: string) => boolean) | null>(null);

  // scheduleSave and runSave call each other (defer while streaming, retry
  // after a failure), so a queued timer reaches the current runSave through a
  // ref instead of the two having to depend on one another.
  const runSaveRef = useRef<(agentId: string) => Promise<void>>(async () => {});

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    persistenceEnabledRef.current = sessionPersistence;
  }, [sessionPersistence]);

  /** Identity the current token belongs to, normalized for comparison. */
  const currentOwner = userInfo?.email?.toLowerCase() ?? null;
  const currentOwnerRef = useRef(currentOwner);
  useEffect(() => {
    currentOwnerRef.current = currentOwner;
  }, [currentOwner]);

  // Helper to trigger component updates
  const triggerUpdate = useCallback(() => {
    setUpdateCounter((prev) => prev + 1);
  }, []);

  const registerStreamingProbe = useCallback((probe: ((agentId: string) => boolean) | null) => {
    streamingProbeRef.current = probe;
  }, []);

  /** Drop any write queued for an agent, and reset its retry budget. */
  const cancelScheduledSave = useCallback((agentId: string) => {
    const timer = saveTimersRef.current.get(agentId);
    if (timer) {
      clearTimeout(timer);
      saveTimersRef.current.delete(agentId);
    }
    saveRetriesRef.current.delete(agentId);
  }, []);

  /**
   * Queue a write of one agent's session, replacing any write already queued
   * for it. Every session mutation funnels through here, which is what makes
   * post-turn changes (apply status, submitted forms, stale markers) persist
   * and not just the end of a turn.
   */
  const scheduleSave = useCallback((agentId: string) => {
    if (!persistenceEnabledRef.current) return;
    if (isUnmountedRef.current) return;

    const existing = saveTimersRef.current.get(agentId);
    if (existing) {
      clearTimeout(existing);
    }

    saveTimersRef.current.set(
      agentId,
      setTimeout(() => {
        void runSaveRef.current(agentId);
      }, SAVE_DEBOUNCE_MS)
    );
  }, []);

  /**
   * Write one agent's session. Best-effort throughout: a rejected payload is
   * logged and abandoned (resending it unchanged produces the same verdict),
   * while a write that never reached the backend is re-armed a few times.
   */
  const runSave = useCallback(
    async (agentId: string): Promise<void> => {
      saveTimersRef.current.delete(agentId);

      if (!persistenceEnabledRef.current) return;

      const token = accessTokenRef.current;
      if (!token) return;

      if (!saveArmedAgentsRef.current.has(agentId)) {
        log.debug("Skipping session save - stored state for this agent is unknown", { agentId });
        return;
      }

      // Refuse to file these conversations under anyone but the account they
      // were held for. Without this a mid-session account switch would write
      // the previous user's transcript under the new user's identity, and the
      // new user would read it back on their next launch.
      if (sessionOwnerRef.current !== null && currentOwnerRef.current !== sessionOwnerRef.current) {
        log.warn("Skipping session save - the signed-in account changed since these were loaded");
        return;
      }

      // Never persist an agent that is mid-turn. A half-streamed message would
      // come back as a truncated answer, and the fields that make a restore
      // correct are only written once the turn ends. The turn is short, so wait
      // it out rather than dropping the write.
      if (streamingProbeRef.current?.(agentId)) {
        // Bounded, because a stream whose completion callback never fires would
        // otherwise keep a timer re-arming for the life of the pane and never
        // persist the turn at all.
        const deferrals = (saveDeferralsRef.current.get(agentId) ?? 0) + 1;
        if (deferrals <= MAX_STREAMING_DEFERRALS) {
          saveDeferralsRef.current.set(agentId, deferrals);
          log.debug("Deferring session save while the agent is generating", {
            agentId,
            deferrals,
          });
          scheduleSave(agentId);
          return;
        }
        log.warn("Saving despite the agent still reporting as generating", { agentId });
      }
      saveDeferralsRef.current.delete(agentId);

      const snapshot = sessionManager.getAgentSessionSnapshot(agentId);
      if (!snapshot) return;

      // Nothing to restore from an empty conversation, and merely opening the
      // pane touches session state (the learning-tip tracker), so without this
      // every launch would write a row for a conversation that never happened.
      // Clearing a conversation deletes the row rather than storing it empty.
      if (snapshot.messages.length === 0) return;

      // Let the in-flight write finish first, then persist whatever the session
      // looks like at that point - which already includes this mutation.
      if (savesInFlightRef.current.has(agentId)) {
        scheduleSave(agentId);
        return;
      }

      const epoch = writeEpochRef.current.get(agentId) ?? 0;
      savesInFlightRef.current.add(agentId);

      let result: SessionWriteResult;
      try {
        result = await saveSessionState(
          agentId,
          {
            // Sent on every write: the backend replaces the whole row, so leaving
            // this out would null the stored chat id and split the next reopen
            // into a new backend session.
            backend_session_id: agentAwareApiAdapter.getAgentSessionId(agentId),
            schema_version: PERSISTED_SESSION_SCHEMA_VERSION,
            payload: buildPersistedPayload(snapshot),
          },
          token
        );
      } finally {
        savesInFlightRef.current.delete(agentId);
      }

      // The conversation was cleared while this write was in flight. Whatever
      // the backend just accepted is a resurrection of a transcript the user
      // deliberately discarded, so re-issue the delete rather than retrying.
      if ((writeEpochRef.current.get(agentId) ?? 0) !== epoch) {
        log.info("Session was cleared while a save was in flight - discarding it again", {
          agentId,
        });
        if (result.kind === "saved") {
          void deleteSessionState(agentId, accessTokenRef.current);
        }
        return;
      }

      if (result.kind === "rejected") {
        // Resending the same payload produces the same verdict, so this agent
        // stops saving until something about the conversation changes.
        track("session:save_rejected", { agent_id: agentId, http_status: result.httpStatus });
        saveRetriesRef.current.delete(agentId);
        return;
      }

      if (result.kind === "failed") {
        const attempts = (saveRetriesRef.current.get(agentId) ?? 0) + 1;
        if (attempts > MAX_CONSECUTIVE_SAVE_RETRIES) {
          log.warn("Giving up on session save after repeated failures", { agentId, attempts });
          track("session:save_failed", { agent_id: agentId, attempts });
          saveRetriesRef.current.delete(agentId);
          return;
        }
        saveRetriesRef.current.set(agentId, attempts);
        scheduleSave(agentId);
        return;
      }

      saveRetriesRef.current.delete(agentId);
    },
    [sessionManager, scheduleSave]
  );

  useEffect(() => {
    runSaveRef.current = runSave;
  }, [runSave]);

  const dismissRestoreNotice = useCallback(() => {
    setShowRestoreNotice(false);
  }, []);

  const deleteStoredSession = useCallback(
    (agentId: string) => {
      cancelScheduledSave(agentId);

      // Invalidate any write already awaiting a response, so it cannot land
      // after this delete and bring the conversation back.
      writeEpochRef.current.set(agentId, (writeEpochRef.current.get(agentId) ?? 0) + 1);

      if (!persistenceEnabledRef.current) return;

      const token = accessTokenRef.current;
      if (!token) return;

      // Clearing a conversation is an explicit user action, so a delete that
      // never reached the backend is worth re-trying: leaving the row behind
      // means the conversation returns on the next launch.
      const attemptDelete = async (attempt: number): Promise<void> => {
        const result = await deleteSessionState(agentId, accessTokenRef.current);
        if (result.kind !== "failed") return;

        if (attempt >= MAX_CONSECUTIVE_SAVE_RETRIES) {
          log.warn("Stored conversation may survive the clear - delete never reached the backend", {
            agentId,
            attempts: attempt,
          });
          return;
        }
        setTimeout(() => void attemptDelete(attempt + 1), SAVE_DEBOUNCE_MS);
      };

      void attemptDelete(1);
    },
    [cancelScheduledSave]
  );

  // Drop every queued write when the provider goes away - a timer that fires
  // after teardown would read a session manager nothing is rendering from.
  useEffect(() => {
    const timers = saveTimersRef.current;
    return () => {
      isUnmountedRef.current = true;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  /**
   * Read every agent's stored conversation and install it. Runs once per
   * launch, before the chat is allowed to paint.
   */
  const restoreStoredSessions = useCallback(async (): Promise<void> => {
    const token = accessTokenRef.current;
    const agentIds = availableAgents.map((agent) => agent.id);

    try {
      const envelopes = await Promise.all(
        agentIds.map(async (agentId) => {
          const read = await fetchSessionState(agentId, token);

          // Only a conclusive answer makes it safe to start replacing this
          // agent's stored row.
          if (read.kind === "failed") {
            log.warn("Could not read stored session - saving stays off for this agent", {
              agentId,
            });
            // The only outward sign that this agent has silently lost
            // persistence for the rest of the session.
            track("session:restore_failed", { agent_id: agentId, reason: "read_failed" });
            return null;
          }
          saveArmedAgentsRef.current.add(agentId);

          if (read.kind === "empty") return null;
          const envelope = read.envelope;

          const snapshot = rehydrateSessionPayload(envelope.payload);
          // An empty transcript is not worth restoring, and treating it as a
          // restore would suppress the launch-time document classification for
          // nothing.
          if (!snapshot || snapshot.messages.length === 0) return null;

          return {
            agentId,
            snapshot,
            backendSessionId: envelope.backend_session_id,
            updatedAt: Date.parse(envelope.updated_at ?? "") || 0,
          };
        })
      );

      const restored = envelopes.filter((entry): entry is NonNullable<typeof entry> => !!entry);

      restored.forEach((entry) => {
        sessionManager.hydrateAgentSession(entry.agentId, entry.snapshot);

        // Keep talking to the same backend conversation, so a reopen extends
        // the existing session in the usage reports instead of starting a new
        // one, and tell the model once that the conversation was reopened.
        // Every file the conversation was answered with, oldest first and
        // de-duplicated. The files themselves are gone, so the model is given
        // their names to account for rather than left to look like it invented
        // them when the user asks a follow-up about one.
        const attachmentNames = Array.from(
          new Set(
            entry.snapshot.messages.flatMap((message) =>
              (message.attachments ?? []).map((attachment) => attachment.name)
            )
          )
        );

        const service = agentModelServiceFactory.getServiceForAgent(entry.agentId);
        if (service && "restoreSession" in service) {
          (service as AgentAwareModelService).restoreSession(
            entry.backendSessionId,
            attachmentNames
          );
        }
      });

      if (restored.length > 0) {
        // Reopen on the conversation the user was last in.
        const mostRecent = restored.reduce((latest, entry) =>
          entry.updatedAt > latest.updatedAt ? entry : latest
        );
        setCurrentAgentById(mostRecent.agentId);
        setRestoredAgentIds(restored.map((entry) => entry.agentId));

        // Once per launch, however many agents came back.
        if (!restoreNoticeShownRef.current) {
          restoreNoticeShownRef.current = true;
          setShowRestoreNotice(true);
        }

        triggerUpdate();

        log.info("Restored stored sessions", {
          agentIds: restored.map((entry) => entry.agentId),
          activeAgentId: mostRecent.agentId,
          messageCounts: restored.map((entry) => entry.snapshot.messages.length),
        });

        restored.forEach((entry) =>
          track("session:restored", {
            agent_id: entry.agentId,
            message_count: entry.snapshot.messages.length,
            age_hours: entry.updatedAt ? Math.round((Date.now() - entry.updatedAt) / 3_600_000) : 0,
          })
        );
      } else {
        log.info("No stored sessions to restore");
      }
    } catch (error) {
      // Persistence is never allowed to hold up the pane; an unusable read just
      // means every agent starts empty.
      log.warn("Session restore failed - starting with empty conversations", {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsSessionRestorePending(false);
    }
  }, [availableAgents, sessionManager, setCurrentAgentById, triggerUpdate]);

  // Launch-time restore. Held until auth has finished and the access gate has
  // answered, because both the token and the entitlement come from there.
  useEffect(() => {
    if (restoreStartedRef.current) return;
    if (!authCompleted) return;
    // A token was acquired but the access gate has not answered yet: the
    // entitlement arrives with that answer.
    if (accessToken !== null && accessDecision === null) return;

    restoreStartedRef.current = true;
    // Whoever is signed in now owns whatever ends up in memory, including the
    // empty conversations we start with when there is nothing to restore.
    sessionOwnerRef.current = currentOwnerRef.current;

    const canRestore = canAccessBackend && accessDecision?.kind === "allowed" && sessionPersistence;

    if (!canRestore) {
      log.debug("Skipping session restore", {
        canAccessBackend,
        accessDecision: accessDecision?.kind ?? "none",
        sessionPersistence,
      });
      setIsSessionRestorePending(false);
      return;
    }

    void restoreStoredSessions();
  }, [
    authCompleted,
    accessToken,
    accessDecision,
    canAccessBackend,
    sessionPersistence,
    restoreStoredSessions,
  ]);

  // A mid-session account switch. The pane does not unmount for this (auth
  // deliberately keeps the tree alive through a refresh), so the persistence
  // lifecycle has to be reset by hand: drop every queued write, forget the
  // previous account's conversations, and let the restore effect run again for
  // whoever is signed in now.
  useEffect(() => {
    const owner = sessionOwnerRef.current;
    if (owner === null || currentOwner === null || currentOwner === owner) return;

    log.info("Signed-in account changed - reloading stored conversations");

    saveTimersRef.current.forEach((timer) => clearTimeout(timer));
    saveTimersRef.current.clear();
    saveRetriesRef.current.clear();
    saveArmedAgentsRef.current.clear();

    availableAgents.forEach((agent) =>
      sessionManager.hydrateAgentSession(agent.id, {
        selectedPlaybook: null,
        seenTipIds: [],
        messages: [],
      })
    );

    sessionOwnerRef.current = null;
    restoreStartedRef.current = false;
    restoreNoticeShownRef.current = false;
    setShowRestoreNotice(false);
    setRestoredAgentIds([]);
    setIsSessionRestorePending(true);
    triggerUpdate();
  }, [currentOwner, availableAgents, sessionManager, triggerUpdate]);

  // Sync agent changes with the session manager
  useEffect(() => {
    // When the agent changes, update the session manager
    sessionManager.setCurrentAgent(currentAgent.id);
    currentAgentIdRef.current = currentAgent.id;

    // Force a re-render to show the new agent's session
    triggerUpdate();

    log.debug("Agent changed", {
      agentId: currentAgent.id,
      agentName: currentAgent.name,
    });
  }, [currentAgent.id, sessionManager, triggerUpdate]);

  // Add a user message
  const addUserMessage = useCallback(
    (content: string, documentContext?: any): ChatMessage => {
      const message = sessionManager.addMessage({
        role: "user",
        content,
        timestamp: Date.now(),
        documentContext,
      });

      triggerUpdate();
      scheduleSave(currentAgentIdRef.current);
      return message;
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Add an assistant message
  const addAssistantMessage = useCallback(
    (content: string): ChatMessage => {
      const message = sessionManager.addMessage({
        role: "assistant",
        content,
        timestamp: Date.now(),
      });

      triggerUpdate();
      scheduleSave(currentAgentIdRef.current);
      return message;
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Update an existing message
  const updateMessage = useCallback(
    (id: string, updates: Partial<Omit<ChatMessage, "id">>): void => {
      sessionManager.updateMessage(id, updates);
      triggerUpdate();
      scheduleSave(currentAgentIdRef.current);
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Add a user message to a specific agent's session
  const addUserMessageForAgent = useCallback(
    (
      agentId: string,
      content: string,
      documentContext?: any,
      action?: UserMessageAction
    ): ChatMessage => {
      const message = sessionManager.addMessageForAgent(agentId, {
        role: "user",
        content,
        timestamp: Date.now(),
        documentContext,
        ...(action && { action }),
      });

      triggerUpdate();
      scheduleSave(agentId);
      return message;
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Add an assistant message to a specific agent's session
  const addAssistantMessageForAgent = useCallback(
    (agentId: string, content: string): ChatMessage => {
      const message = sessionManager.addMessageForAgent(agentId, {
        role: "assistant",
        content,
        timestamp: Date.now(),
      });

      triggerUpdate();
      scheduleSave(agentId);
      return message;
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Update a message in a specific agent's session
  const updateMessageForAgent = useCallback(
    (agentId: string, id: string, updates: Partial<Omit<ChatMessage, "id">>): void => {
      sessionManager.updateMessageForAgent(agentId, id, updates);
      triggerUpdate();
      scheduleSave(agentId);
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Clear the conversation
  const clearConversation = useCallback(() => {
    sessionManager.clearSession();
    triggerUpdate();

    // Drop any write still queued for this agent. Without this the debounce
    // would fire moments later and re-create the row the caller is about to
    // delete, with an empty transcript.
    cancelScheduledSave(currentAgentIdRef.current);
  }, [sessionManager, triggerUpdate, cancelScheduledSave]);

  // Remove the last message pair
  const removeLastMessagePair = useCallback(() => {
    sessionManager.removeLastMessagePair();
    triggerUpdate();
    scheduleSave(currentAgentIdRef.current);
  }, [sessionManager, triggerUpdate, scheduleSave]);

  // Remove the last message pair for a specific agent (for parallel agent support)
  const removeLastMessagePairForAgent = useCallback(
    (agentId: string) => {
      sessionManager.removeLastMessagePairForAgent(agentId);
      triggerUpdate();
      scheduleSave(agentId);
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Get a specific agent's session (for parallel agent support)
  const getAgentSession = useCallback(
    (agentId: string): ChatSession | undefined => {
      return sessionManager.getAgentSession(agentId);
    },
    [sessionManager]
  );

  // Get the conversation history
  const getConversationHistory = useCallback(() => {
    return sessionManager.getConversationHistory();
  }, [sessionManager]);

  // Set the contract type
  const setContractType = useCallback(
    (contractType: string) => {
      sessionManager.setContractType(contractType);
      triggerUpdate();
      scheduleSave(currentAgentIdRef.current);
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Get the contract type
  const getContractType = useCallback(() => {
    return sessionManager.getContractType();
  }, [sessionManager]);

  // Set the selected playbook
  const setSelectedPlaybook = useCallback(
    (playbook: { id: string; name: string } | null) => {
      sessionManager.setSelectedPlaybook(playbook);
      triggerUpdate();
      scheduleSave(currentAgentIdRef.current);
    },
    [sessionManager, triggerUpdate, scheduleSave]
  );

  // Get the selected playbook
  const getSelectedPlaybook = useCallback(() => {
    return sessionManager.getSelectedPlaybook();
  }, [sessionManager]);

  // Learning tip tracking methods
  const markTipAsSeen = useCallback(
    (tipId: string) => {
      sessionManager.markTipAsSeen(tipId);
      scheduleSave(currentAgentIdRef.current);
    },
    [sessionManager, scheduleSave]
  );

  const getSeenTipIds = useCallback(() => {
    return sessionManager.getSeenTipIds();
  }, [sessionManager]);

  const resetSeenTips = useCallback(() => {
    sessionManager.resetSeenTips();
    scheduleSave(currentAgentIdRef.current);
  }, [sessionManager, scheduleSave]);

  // Get the current session (for rendering) - memoized to prevent race conditions
  const currentSession = useMemo(() => {
    // This ensures the session is properly cached and updates only when needed
    return sessionManager.getCurrentSession();
  }, [sessionManager, currentAgent.id, updateCounter]);

  // Context value
  const contextValue: SessionContextType = {
    currentSession,
    addUserMessage,
    addAssistantMessage,
    addUserMessageForAgent,
    addAssistantMessageForAgent,
    updateMessage,
    updateMessageForAgent,
    clearConversation,
    removeLastMessagePair,
    removeLastMessagePairForAgent,
    getAgentSession,
    setContractType,
    getContractType,
    setSelectedPlaybook,
    getSelectedPlaybook,
    markTipAsSeen,
    getSeenTipIds,
    resetSeenTips,
    getConversationHistory,
    isSessionRestorePending,
    hasRestoredSession: restoredAgentIds.length > 0,
    registerStreamingProbe,
    deleteStoredSession,
    showRestoreNotice,
    dismissRestoreNotice,
  };

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};

/**
 * Custom hook to use the session context
 */
export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);

  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }

  return context;
};
